import { DocumentType } from '@synergy/models';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Localized } from '@synergy/components';
import { PaloColumn, PaloFilterChips, PaloListView, PaloRowAction, PaloStatusPill } from '@synergy/components-palo';
import { PoolDocumentTitle } from '@synergy/components-next';
import { ReactElement, useEffect, useState } from 'react';
import { getDocumentStatusTranslationId } from '@synergy/utils';
import { useNavigate } from 'react-router';

import { ContactPresenter } from '../../../../common/components/contact-presenter/contact-presenter';
import { ConverterReceiptModal } from '../../../convert-receipt/converter-receipt-modal';
import { Demand } from '../../../demands/models/demand';
import { DemandTransition } from '../../../convert-receipt/models/demand/demandTransition';
import { Transition } from '../../../convert-receipt/models/type-transition';
import { TransitionTarget } from '../../../convert-receipt/models/internal/transition-target';
import {
    abortScheduledDemand,
    deleteDemand,
    requestEnd as demandRequestEnd,
    resetDemand,
} from '../../../demands/demand-actions';
import { fetchDemandTransitionData } from '../../../demands/containers/detail/store/demand-detail-actions';
import { fetchTransitions, resetTransitions } from '../../../../common/actions/transitions-activity-actions';
import { requestingSelector } from '../../store/dashboard-selectors';
import { transitionDataSelector } from '../../../demands/containers/detail/store/demand-detail-selectors';
import {
    transitionDemandIntoInquiries,
    transitionDemandIntoPurchaseOrders,
} from '../../../convert-receipt/transition-actions';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useDashboardSort } from '../../palo/use-dashboard-sort';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const columns: PaloColumn<Demand>[] = [
    {
        key: 'number',
        type: 'documentNumber',
        sortKey: 'number',
        head: <FormattedMessage id="app.table.column.number" />,
        cell: demand => demand.number,
    },
    {
        key: 'status',
        type: 'status',
        sortKey: 'status',
        head: <FormattedMessage id="app.table.column.status" />,
        cell: demand => (
            <PaloStatusPill
                status={demand.status}
                label={<FormattedMessage id={getDocumentStatusTranslationId(demand.status)} />}
            />
        ),
    },
    {
        key: 'title',
        type: 'name',
        sortKey: 'title',
        head: <FormattedMessage id="app.table.column.title" />,
        cell: demand => <Localized value={demand.title} />,
    },
    {
        key: 'responsibleContact',
        type: 'name',
        sortKey: 'responsibleContact',
        head: <FormattedMessage id="app.common.responsibleContact" />,
        cell: demand => (
            <ContactPresenter
                person={demand.responsibleContact}
                fullName={demand.responsibleContact?.fullName}
                isDeleted={demand.responsibleContact?.isDeleted}
                isLocked={demand.responsibleContact?.isLocked}
            />
        ),
    },
    {
        key: 'createdAt',
        type: 'date',
        sortKey: 'createdAt',
        head: <FormattedMessage id="app.table.column.createdAt" />,
        cell: demand => <FormattedDate value={demand.createdAt} year="numeric" month="short" day="2-digit" />,
    },
];

export const DashboardOverviewDemands = (): ReactElement => {
    const navigate = useNavigate();
    const { sort, onSortChange } = useDashboardSort('demands');
    const dispatch = useThunkDispatch();
    const [isConverterVisible, setIsConverterVisible] = useState(false);
    const transitionData = useAppSelector(transitionDataSelector);
    const demands = useAppSelector(state => state.demands.flat);
    const requesting = useAppSelector(requestingSelector);

    const cleanupState = (): void => {
        dispatch(resetDemand());
        dispatch(demandRequestEnd());
    };

    useEffect(() => {
        return cleanupState;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleConvertOpen = (id: number): void => {
        dispatch(resetDemand());
        dispatch(resetTransitions());
        dispatch(fetchTransitions(DocumentType.Demand, id));
        dispatch(fetchDemandTransitionData(id));
        setIsConverterVisible(true);
    };

    const handleTransition = (transitionObject: Transition, target: TransitionTarget) =>
        target === TransitionTarget.PurchaseOrder
            ? dispatch(transitionDemandIntoPurchaseOrders(transitionObject as DemandTransition))
            : dispatch(transitionDemandIntoInquiries(transitionObject as DemandTransition));

    const handleConvertClose = (): void => {
        cleanupState();
        setIsConverterVisible(false);
    };

    const rowActions = (demand: Demand): PaloRowAction[] => [
        { label: 'Öffnen', onClick: () => navigate('/demands/' + demand.id) },
        { label: 'Umwandeln', onClick: () => handleConvertOpen(demand.id) },
        { label: 'Abbrechen', onClick: () => dispatch(abortScheduledDemand(demand.id)) },
        { label: 'Löschen', onClick: () => dispatch(deleteDemand(demand.id)) },
    ];

    return (
        <>
            <PoolDocumentTitle titleTranslationId="app.navigation.dashboard.demands" />
            <PaloFilterChips />
            <PaloListView<Demand>
                embedded
                columns={columns}
                sort={sort}
                onSortChange={onSortChange}
                items={demands.items}
                getRowId={demand => demand.id}
                requesting={requesting}
                emptyLabel="Kein Bedarf."
                onRowClick={demand => navigate('/demands/' + demand.id)}
                rowActions={rowActions}
            />
            {transitionData && (
                <ConverterReceiptModal
                    isVisible={isConverterVisible}
                    source={transitionData}
                    onClose={handleConvertClose}
                    onConvert={handleTransition}
                />
            )}
        </>
    );
};
